import { WA_WINDOW } from '@ng-web-apis/common';
import { TuiTable } from '@taiga-ui/addon-table';
import { pure } from '@core/helpers/pure';
// angular and rxjs imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';

// 3rd-party imports
import { TuiBadge } from '@taiga-ui/kit';
import {
  TuiIcon,
  TuiHint,
  TuiCheckbox,
  TuiDialogService,
} from '@taiga-ui/core';
// local imports
import { RequestModel } from '@core/models';
import { RequestProductIconSize } from '@features/requests/request-product-icon/types';

import {
  Counterparty,
  RequestFile,
  RequestsListItemTranslations,
  RequestsListItemTranslationsPayload,
  RequestStatusName,
  TextCopierTranslations,
  TextCopierTranslationsPayload,
} from '@core/types';
import { RequestReleaseTimeParserPipe } from '@core/pipes';
import { FileDownloadService } from '@core/services/file-download.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { RequestService } from '@core/services/request/request.service';
import { XAlertService } from '@core/services/x-alert.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestProductIconComponent } from '@features/requests/request-product-icon/request-product-icon.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
  counterpartyIconResolver,
  leaseProductIconResolver,
  Nullable,
} from '@core/helpers';
import { IconButtonComponent, SvgIconComponent } from '@common/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environment/environment';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { ClipboardService } from 'ngx-clipboard';
import { SelectedIds } from '@common/types';
import { LogisticRequestType, OutkeepPermission } from '@outkeep/shared';
import { catchError, EMPTY, switchMap } from 'rxjs';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TemporaryAccessDialogComponent } from '../temporary-access-dialog/temporary-access-dialog.component';

@Component({
  selector: '[x-request-list-item-desktop]',
  imports: [
    SvgIconComponent,
    CommonModule,
    RequestProductIconComponent,
    TuiBadge,
    TuiIcon,
    TuiTable,
    TuiCheckbox,
    FormsModule,
    RouterLink,
    RequestReleaseTimeParserPipe,
    TuiHint,
    TranslatePipe,
    NgOptimizedImage,
    IconButtonComponent,
  ],
  templateUrl: './request-list-item-desktop.component.html',
  styleUrl: './request-list-item-desktop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestListItemDesktopComponent implements OnInit, OnDestroy {
  public readonly requestService = inject(RequestService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly clipboardService = inject(ClipboardService);
  private readonly xAlertService = inject(XAlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly window = inject(WA_WINDOW);

  $desktopTableColumns = input<string[]>([], { alias: 'desktopTableColumns' });
  $isRepossessionMode = input(false, { alias: 'isRepossessionMode' });

  $request = input.required<RequestModel>({ alias: 'request' });
  $selectedRequestsIds: Signal<SelectedIds | null> | undefined = undefined;

  // Templates for desktop table columns
  @ViewChild('requestNumber', { static: true })
  requestNumber!: TemplateRef<any>;

  @ViewChild('releaseTime', { static: true })
  releaseTimeTemp!: TemplateRef<any>;

  @ViewChild('releaseDate', { static: true })
  releaseDateTemp!: TemplateRef<any>;

  @ViewChild('releaseDatePeriod', { static: true })
  releaseDatePeriodTemp!: TemplateRef<any>;

  @ViewChild('contract', { static: true })
  contractTemp!: TemplateRef<any>;

  @ViewChild('carModelAndBrand', { static: true })
  carModelAndBrandTemp!: TemplateRef<any>;

  @ViewChild('carPlantNumber', { static: true })
  carPlantNumberTemp!: TemplateRef<any>;

  @ViewChild('vin', { static: true })
  vinTemp!: TemplateRef<any>;

  @ViewChild('projectNumber', { static: true })
  projectNumberTemp!: TemplateRef<any>;

  @ViewChild('subjName', { static: true })
  subjNameTemp!: TemplateRef<any>;

  @ViewChild('deliveryAddress', { static: true })
  deliveryAddressTemp!: TemplateRef<any>;

  @ViewChild('counterparty', { static: true })
  counterpartyTemp!: TemplateRef<any>;

  @ViewChild('requestProduct', { static: true })
  requestProductTemp!: TemplateRef<any>;

  @ViewChild('installers', { static: true })
  installersTemp!: TemplateRef<any>;

  @ViewChild('requestStatus', { static: true })
  requestStatusTemp!: TemplateRef<any>;

  @ViewChild('deliveryName', { static: true })
  deliveryNameTemp!: TemplateRef<any>;

  @ViewChild('createdAt', { static: true })
  createdAtTemp!: TemplateRef<any>;

  @ViewChild('updatedAt', { static: true })
  updatedAtTemp!: TemplateRef<any>;

  @ViewChild('qualityControlStatus', { static: true })
  qualityControlStatusTemp!: TemplateRef<any>;

  @ViewChild('docs', { static: true })
  docsTemp!: TemplateRef<any>;

  public columnsMap: Nullable<Record<string, TemplateRef<any>>> = null;

  // END Templates for desktop table columns

  public RequestStatusNameEnum = RequestStatusName;

  public selectRequestEvent = output<number>();

  protected readonly RequestProductIconSize = RequestProductIconSize;
  readonly canGenerateGuestLink = this.permissionService.$hasPermission(
    OutkeepPermission.LogistViewRepossessionRequests,
  );
  readonly canCreateTemporaryAccess = this.permissionService.$hasPermission(
    OutkeepPermission.LogistCreateTemporaryAccess,
  );
  protected readonly $repossessionActionsEnabled = computed(
    () =>
      this.$isRepossessionMode() &&
      this.$request().requestType === LogisticRequestType.Repossession &&
      this.canGenerateGuestLink(),
  );

  public lPPhoto: WritableSignal<Nullable<RequestFile>> = signal(null);
  public ePTS: WritableSignal<Nullable<RequestFile>> = signal(null);
  public appKDkp: WritableSignal<Nullable<RequestFile>> = signal(null);
  public appKDl: WritableSignal<Nullable<RequestFile>> = signal(null);

  public today = new Date();

  private translates: Partial<RequestsListItemTranslationsPayload> &
    TextCopierTranslationsPayload = {
    [RequestsListItemTranslations.DocNotFound]: 'Документ не найден',
    [RequestsListItemTranslations.GuestLinkCopied]:
      'Гостевая ссылка скопирована',
    [RequestsListItemTranslations.GuestLinkFailed]:
      'Не удалось создать гостевую ссылку',
    [RequestsListItemTranslations.RevokeGuestLinks]:
      'Отозвать все гостевые ссылки',
    [RequestsListItemTranslations.RevokeGuestLinksConfirmTitle]:
      'Отозвать гостевые ссылки',
    [RequestsListItemTranslations.RevokeGuestLinksConfirmText]:
      'Все ранее выпущенные гостевые ссылки для этой заявки перестанут работать. Продолжить?',
    [RequestsListItemTranslations.RevokeGuestLinksSuccess]:
      'Гостевые ссылки отозваны',
    [RequestsListItemTranslations.RevokeGuestLinksFailed]:
      'Не удалось отозвать гостевые ссылки',
    [TextCopierTranslations.Copied]: 'Скопировано',
    [TextCopierTranslations.NotFoundToCopy]: 'Нечего копировать',
  };

  constructor() {
    effect(() => {
      if (this.requestFilesService.loadedRequestsMainFiles()) {
        const mainFiles =
          this.requestFilesService.loadedRequestsMainFiles()[
            this.$request().id
          ];

        if (mainFiles) {
          this.appKDkp.set(
            mainFiles['obrz_app_k_dkp']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.appKDl.set(
            mainFiles['obrz_app_k_dl']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.lPPhoto.set(
            mainFiles['photo_pl']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.ePTS.set(
            mainFiles['obrz_pts_psm']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
        }
      }
    });
  }

  ngOnInit(): void {
    this.$selectedRequestsIds = computed(() =>
      this.requestService.selectedRequestsIds(),
    );

    this.outkeepTranslateService
      .translate(
        Object.values({
          ...RequestsListItemTranslations,
          ...TextCopierTranslations,
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (
          translates: RequestsListItemTranslationsPayload &
            TextCopierTranslationsPayload,
        ) => (this.translates = translates),
      );

    this.columnsMap = {
      requestNumber: this.requestNumber,
      releaseTime: this.releaseTimeTemp,
      releaseDate: this.releaseDateTemp,
      releaseDatePeriod: this.releaseDatePeriodTemp,
      contract: this.contractTemp,
      car: this.carModelAndBrandTemp,
      carPlantNumber: this.carPlantNumberTemp,
      vin: this.vinTemp,
      projectNumber: this.projectNumberTemp,
      subjName: this.subjNameTemp,
      deliveryAddress: this.deliveryAddressTemp,
      counterPartyName: this.counterpartyTemp,
      products: this.requestProductTemp,
      installerShort: this.installersTemp,
      statusId: this.requestStatusTemp,
      deliveryName: this.deliveryNameTemp,
      createdAt: this.createdAtTemp,
      updatedAt: this.updatedAtTemp,
      qualityControlStatus: this.qualityControlStatusTemp,
      docs: this.docsTemp,
    };
  }

  ngOnDestroy(): void {
    this.$selectedRequestsIds = undefined;
  }

  onSelectRequest() {
    this.selectRequestEvent.emit(this.$request().id);
  }

  @pure
  getQualityControlClass(statusId?: number): {
    title: string;
    class: string;
  } {
    switch (statusId) {
      case 5:
        return {
          title: 'partially_fixed_errors',
          class: 'bg-danger',
        };
      case 4:
        return {
          title: 'with_fixed_errors',
          class: 'lazur-bg',
        };
      case 3:
        return {
          title: 'with_critical_errors',
          class: 'red-bg',
        };
      case 2:
        return {
          title: 'with_errors',
          class: 'yellow-bg',
        };
      case 1:
        return {
          title: 'without_errors',
          class: 'navy-bg',
        };
      default:
        return {
          title: 'not_verified',
          class: 'gray-bg',
        };
    }
  }

  @pure
  public requestIconResolver(cpiD: Counterparty['id']) {
    return counterpartyIconResolver(cpiD);
  }

  @pure
  public requestLeaseProductIconResolver(
    carType?: RequestModel['carType'],
    carModel?: RequestModel['carModel'],
  ) {
    return leaseProductIconResolver(carType, carModel);
  }

  public createLink(doc: 'epts' | 'lPPhoto' | 'appKDl' | 'appKDkp'): void {
    let file: Nullable<RequestFile> = null;

    switch (doc) {
      case 'epts':
        file = this.ePTS();
        break;
      case 'lPPhoto':
        file = this.lPPhoto();
        break;
      case 'appKDl':
        file = this.appKDl();
        break;
      case 'appKDkp':
        file = this.appKDkp();
        break;
      default:
        break;
    }

    if (!file) {
      this.xAlertService.error(
        this.translates[RequestsListItemTranslations.DocNotFound]!,
      );

      return;
    }

    if (file.filename.toLowerCase().endsWith('.pdf')) {
      this.fileDownloadService.openPdfByPath(
        file.path,
        file.filename || file.path.split('/').pop() || file.path,
      );

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file!.path,
      file!.filename || file!.path.split('/').pop() || file!.path,
      false,
      this.renderer,
    );
  }

  copyToClipboard(textToCopy: string, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    if (!textToCopy) {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.NotFoundToCopy],
        {
          appearance: 'warning',
          autoClose: 2000,
        },
      );

      return;
    }
    this.clipboardService.copy(textToCopy);
    this.xAlertService.showMessage(
      this.translates[TextCopierTranslations.Copied],
      {
        appearance: 'positive',
        autoClose: 2000,
      },
    );
  }

  issueGuestLink(requestNumber: string, event?: MouseEvent): void {
    event?.stopPropagation();

    this.requestsApiService
      .createRestrictedRequestGuestLink$(requestNumber)
      .pipe(
        catchError(() => {
          this.xAlertService.error(
            this.translates[RequestsListItemTranslations.GuestLinkFailed]!,
          );

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(link => {
        const absoluteUrl = new URL(link.url, this.window.location.origin).href;

        this.clipboardService.copy(absoluteUrl);
        this.xAlertService.success(
          this.translates[RequestsListItemTranslations.GuestLinkCopied]!,
          new Date(link.expiresAt).toLocaleString(
            this.outkeepTranslateService.getLocaleStr(),
          ),
          4000,
        );
      });
  }

  revokeGuestLinks(requestNumber: string, event?: MouseEvent): void {
    event?.stopPropagation();

    this.xAlertService
      .showDialog(
        this.translates[
          RequestsListItemTranslations.RevokeGuestLinksConfirmTitle
        ]!,
        this.translates[
          RequestsListItemTranslations.RevokeGuestLinksConfirmText
        ]!,
      )
      .pipe(
        switchMap(result => {
          if (!result) {
            return EMPTY;
          }

          return this.requestsApiService.revokeRestrictedRequestGuestLinks$(
            requestNumber,
          );
        }),
        catchError(() => {
          this.xAlertService.error(
            this.translates[
              RequestsListItemTranslations.RevokeGuestLinksFailed
            ]!,
          );

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        this.xAlertService.success(
          this.translates[
            RequestsListItemTranslations.RevokeGuestLinksSuccess
          ]!,
          `${result.revokedCount}`,
          4000,
        );
      });
  }

  getEyeHref(
    requestNumber: string,
    repossessionActionsEnabled = this.$repossessionActionsEnabled(),
  ): string {
    if (repossessionActionsEnabled) {
      return `/request-view/${requestNumber}`;
    }

    return `/requests/${requestNumber}`;
  }

  openTemporaryAccessDialog(requestId: number, event?: MouseEvent): void {
    event?.stopPropagation();

    this.dialogService
      .open(new PolymorpheusComponent(TemporaryAccessDialogComponent), {
        data: { requestIds: [requestId] },
        dismissible: true,
        size: null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
